"use client";

import { useAuth, useSignIn, useSignUp } from "@clerk/nextjs";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ONBOARDING_FORM_INPUT_CLASSNAME } from "@/lib/onboardingFormInputStyles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { taskSuccess, taskError } from "@/lib/toast";
// `@sukha/shared`, not `@/lib/tolt/attribution` — this branch moved the tolt
// helper into the shared package. The three below arrived with the funnel work.
import { getToltSignupUnsafeMetadata } from "@sukha/shared";
import { landingEventProperties } from "@/lib/analytics/landingCookie";
import { getLandingSignupUnsafeMetadata } from "@/lib/analytics/landingSignup";
import { useIsMobile } from "@/hooks/useIsMobile";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { useTranslations } from "next-intl";
import { trackEventWithDelay } from "@/lib/utils/analytics";
import Logger from "@/lib/utils/logger";
function getClerkFailure(error: unknown): {
  code?: string;
  message: string;
} | null {
  if (!error || typeof error !== "object") {
    return null;
  }
  const e = error as {
    errors?: ClerkErrorItem[];
    message?: string;
  };
  const first = e.errors?.[0];
  if (!first && !e.message) {
    return null;
  }
  return {
    code: first?.code,
    message: first?.longMessage || first?.message || e.message || "Error"
  };
}
function isFormIdentifierNotFound(error: unknown): boolean {
  return getClerkFailure(error)?.code === "form_identifier_not_found";
}
function isFormIdentifierExists(error: unknown): boolean {
  return getClerkFailure(error)?.code === "form_identifier_exists";
}
type ClerkErrorItem = {
  code: string;
  message: string;
  longMessage?: string;
  meta?: Record<string, unknown>;
};
type ClerkError = Error & {
  errors?: ClerkErrorItem[];
};
export type AuthState = "email" | "otp";
interface EmailAuthFlowProps {
  /** Called when email is submitted successfully */
  onEmailSubmit?: (email: string) => void;
  /** Called when OTP is verified successfully */
  onOtpVerified?: () => void;
  /** Current auth mode */
  mode?: "sign-in" | "sign-up";
  /** Callback when mode changes */
  onModeChange?: (mode: "sign-in" | "sign-up") => void;
  /** Use conversational mode (shows previous steps as read-only) */
  conversational?: boolean;
  /** Initial view state (controlled from parent) */
  initialView?: AuthState;
  /** Pre-filled email for OTP view */
  prefilledEmail?: string;
  /** Callback to go back to email step */
  onBackToEmail?: () => void;
  /** Hide the read-only email section (when parent controls navigation) */
  hideReadOnlyEmail?: boolean;
  /** Path opened after OTP succeeds (full page navigation after `setActive`). */
  postOtpRedirectPath?: string;
}

/**
 * Animation variants for steps
 */
const stepVariants = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3
    }
  },
  exit: {
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.2
    }
  }
};

/**
 * Email authentication flow component.
 * In conversational mode, shows previous steps as read-only and scrolls new steps in.
 */
export function EmailAuthFlow({
  onEmailSubmit,
  onOtpVerified,
  mode: propMode = "sign-in",
  onModeChange,
  conversational = true,
  initialView = "email",
  prefilledEmail,
  onBackToEmail,
  hideReadOnlyEmail = false,
  postOtpRedirectPath = "/onboarding/check"
}: EmailAuthFlowProps) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  // initial value: an email handed in from the previous screen; the member submits a new one here
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(prefilledEmail || null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const t = useTranslations("Authentication");
  const {
    isMobileDevice
  } = useIsMobile();
  /** Source page, CTA and device, so the funnel can group the email arm by marketing page. */
  const signupEventProperties = () => ({
    ...landingEventProperties(),
    device: isMobileDevice ? "mobile" : "desktop"
  });

  // Current view state (email input or OTP) - use initialView from parent if provided
  // initial value: initialView is the screen to start on; the flow moves on from there
  const [currentView, setCurrentView] = useState<AuthState>(initialView);

  // Validate email on change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !validateEmail(value)) {
      setEmailError(t("landing.emailInvalid"));
    } else {
      setEmailError(null);
    }
  };

  // Email validation function
  const validateEmail = (email_0: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email_0);
  };
  const isEmailValid = email && validateEmail(email);
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Handle mode changes
  const setMode = (newMode: "sign-in" | "sign-up") => {
    onModeChange?.(newMode);
  };
  const {
    isLoaded: isAuthLoaded
  } = useAuth();
  const {
    signIn
  } = useSignIn();
  const {
    signUp
  } = useSignUp();
  const authReady = isAuthLoaded && signIn != null && signUp != null;

  // Focus email input on mount
  useEffect(() => {
    if (currentView === "email") {
      const timer = setTimeout(() => {
        emailInputRef.current?.focus({
          preventScroll: true
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [currentView]);

  // Focus OTP input when view changes to OTP
  useEffect(() => {
    if (currentView === "otp") {
      const timer_0 = setTimeout(() => {
        otpInputRef.current?.focus({
          preventScroll: true
        });
      }, 300); // Slightly longer delay for animation
      return () => clearTimeout(timer_0);
    }
  }, [currentView]);
  const handleEmailSubmit = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    if (!email || !authReady || !signIn || !signUp) return;
    setIsLoading(true);
    const goToSignInOtp = () => {
      setMode("sign-in");
      setSubmittedEmail(email);
      setCurrentView("otp");
      onEmailSubmit?.(email);
      taskSuccess(t("landing.verificationCodeSent"));
    };
    const goToSignUpOtp = () => {
      setMode("sign-up");
      setSubmittedEmail(email);
      setCurrentView("otp");
      onEmailSubmit?.(email);
      taskSuccess(t("landing.verificationCodeSent"));
    };
    const trySignUpOrRecoverSignIn = async (): Promise<void> => {
      const su = await signUp.create({
        emailAddress: email,
        legalAccepted: true,
        unsafeMetadata: {
          ...getToltSignupUnsafeMetadata(),
          ...getLandingSignupUnsafeMetadata("email")
        }
      });
      if (su.error) {
        if (isFormIdentifierExists(su.error)) {
          const retryCreate = await signIn.create({
            identifier: email
          });
          if (retryCreate.error) {
            taskError(getClerkFailure(retryCreate.error)?.message ?? t("landing.error"));
            return;
          }
          const sentExisting = await signIn.emailCode.sendCode({
            emailAddress: email
          });
          if (sentExisting.error) {
            taskError(getClerkFailure(sentExisting.error)?.message ?? t("landing.error"));
            return;
          }
          goToSignInOtp();
          return;
        }
        taskError(getClerkFailure(su.error)?.message ?? t("landing.error"));
        return;
      }
      const sentUp = await signUp.verifications.sendEmailCode();
      if (sentUp.error) {
        taskError(getClerkFailure(sentUp.error)?.message ?? t("landing.error"));
        return;
      }
      goToSignUpOtp();
    };
    try {
      const created = await signIn.create({
        identifier: email
      });
      if (created.error) {
        if (isFormIdentifierNotFound(created.error)) {
          await trySignUpOrRecoverSignIn();
          return;
        }
        taskError(getClerkFailure(created.error)?.message ?? t("landing.error"));
        return;
      }
      const sent = await signIn.emailCode.sendCode({
        emailAddress: email
      });
      if (sent.error) {
        if (isFormIdentifierNotFound(sent.error)) {
          await trySignUpOrRecoverSignIn();
          return;
        }
        taskError(getClerkFailure(sent.error)?.message ?? t("landing.error"));
        return;
      }
      goToSignInOtp();
    } catch (error: unknown) {
      Logger.error("[EmailAuthFlow] Authentication error:", error);
      const clerkError = error as ClerkError;
      const errorMessage = clerkError.errors?.[0]?.longMessage || getClerkFailure(error)?.message || t("landing.error");
      taskError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  const [otpError, setOtpError] = useState<string | null>(null);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const otpVerifySucceededRef = useRef(false);
  const buildPostAuthNavigate = (successMessage: string) => ((opts: unknown) => {
    const o = opts as {
      decorateUrl?: (path: string) => string;
    };
    taskSuccess(successMessage);
    onOtpVerified?.();
    window.location.assign(typeof o.decorateUrl === "function" ? o.decorateUrl(postOtpRedirectPath) : postOtpRedirectPath);
  }) as never;
  const handleOtpSubmit = async (otpValue: string) => {
    if (!authReady || !signIn || !signUp) return;
    otpVerifySucceededRef.current = false;
    setIsLoading(true);
    setOtpError(null);
    try {
      if (propMode === "sign-in") {
        const {
          error: error_1
        } = await signIn.emailCode.verifyCode({
          code: otpValue
        });
        if (error_1) {
          throw error_1;
        }
        if (signIn.status !== "complete") {
          Logger.error("[EmailAuthFlow] Sign-in incomplete after OTP", {
            status: signIn.status
          });
          taskError(t("landing.error"));
          return;
        }
        otpVerifySucceededRef.current = true;
        setIsOtpVerified(true);
        setIsLoading(false);
        // Same screen as a sign-up's code entry; the funnel reads `mode` to
        // keep the two apart.
        await trackEventWithDelay("215-uv_successfully_enters_otp_and_advances", {
          ...signupEventProperties(),
          mode: "sign-in"
        });
        const fin = await signIn.finalize({
          navigate: buildPostAuthNavigate(t("landing.success"))
        });
        if (fin.error) {
          Logger.error("[EmailAuthFlow] finalize after sign-in:", fin.error);
          window.location.href = "/";
        }
        return;
      }
      if (propMode === "sign-up") {
        const {
          error: error_2
        } = await signUp.verifications.verifyEmailCode({
          code: otpValue
        });
        if (error_2) {
          throw error_2;
        }
        if (signUp.status !== "complete") {
          Logger.error("[EmailAuthFlow] Sign-up incomplete after OTP", {
            status: signUp.status
          });
          taskError(t("landing.error"));
          return;
        }
        otpVerifySucceededRef.current = true;
        setIsOtpVerified(true);
        setIsLoading(false);
        // The OTP-verified stage of the funnel's email arm.
        await trackEventWithDelay("215-uv_successfully_enters_otp_and_advances", {
          ...signupEventProperties(),
          mode: "sign-up"
        });
        const finUp = await signUp.finalize({
          navigate: buildPostAuthNavigate(t("landing.accountVerified"))
        });
        if (finUp.error) {
          Logger.error("[EmailAuthFlow] finalize after sign-up:", finUp.error);
          window.location.href = "/onboarding";
        }
        return;
      }
    } catch (error_0) {
      Logger.error("[EmailAuthFlow] Error:", error_0);
      setIsOtpVerified(false);
      setOtpError(t("landing.invalidCode"));
      // Don't clear the OTP, let the user edit it
      taskError(t("landing.invalidCode"));

      // Focus back on the OTP input after a small delay
      setTimeout(() => {
        if (otpInputRef.current) {
          otpInputRef.current.focus({
            preventScroll: true
          });
          // Move cursor to the end
          const length = otpValue.length;
          otpInputRef.current.setSelectionRange(length, length);
        }
      }, 100);
    } finally {
      if (!otpVerifySucceededRef.current) {
        setIsLoading(false);
      }
    }
  };
  const handleBackToEmail = () => {
    setOtp("");
    setIsOtpVerified(false);
    setSubmittedEmail(null);
    setCurrentView("email");
    // Call parent callback if provided
    if (onBackToEmail) {
      onBackToEmail();
    } else {
      // Focus email input after a small delay
      setTimeout(() => {
        emailInputRef.current?.focus({
          preventScroll: true
        });
      }, 100);
    }
  };

  // Conversational mode: render with previous steps visible
  if (conversational) {
    return <div className="space-y-4 w-full max-w-sm">
        <AnimatePresence mode="popLayout">
          {/* Show read-only email input if we've moved to OTP (only if not hidden by parent) */}
          {currentView === "otp" && submittedEmail && !hideReadOnlyEmail && <motion.div key="email-readonly" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-4">
              <Input type="email" value={submittedEmail} readOnly disabled className="opacity-60 cursor-not-allowed rounded-lg" />
              <div className="flex justify-end">
                <div className="bg-secondary-400/30 px-6 py-2 rounded-full opacity-60">
                  <span className="text-primary-foreground text-sm font-medium">{t("landing.continueButton")}</span>
                </div>
              </div>
            </motion.div>}

          {/* OTP section */}
          {currentView === "otp" && submittedEmail && <motion.div key="otp-section" variants={stepVariants} initial="initial" animate="animate" exit="exit" className="space-y-4">
              {otpError && !isOtpVerified && <p className="text-destructive text-sm text-center">{otpError}</p>}

              {isOtpVerified ? <div className="flex min-h-10 flex-col items-center justify-center gap-2" role="status" aria-live="polite">
                  <span className="inline-flex items-center justify-center gap-2 text-primary-foreground text-sm font-medium">
                    <Check className="size-5 shrink-0 text-green-500" aria-hidden="true" />
                    {t("landing.codeVerified")}
                  </span>
                </div> : <>
                  <div className="flex justify-center text-primary-foreground">
                    <InputOTP ref={otpInputRef} maxLength={6} value={otp} onChange={value_0 => {
                setOtp(value_0);
                // Clear error when user starts typing
                if (otpError && value_0.length > 0) {
                  setOtpError(null);
                }
                if (value_0.length === 6) {
                  handleOtpSubmit(value_0);
                }
              }} disabled={isLoading} className={otpError ? "border-destructive" : ""}>
                      {[...Array(6)].map((_, i) => <InputOTPGroup key={i}>
                          <InputOTPSlot className={`rounded-lg border-l ${otpError ? "border-destructive" : ""}`} index={i} />
                        </InputOTPGroup>)}
                    </InputOTP>
                  </div>

                  <div className="flex min-h-5 items-center justify-center" role={isLoading ? "status" : undefined} aria-live={isLoading ? "polite" : undefined} aria-busy={isLoading ? true : undefined}>
                    {isLoading ? <span className="inline-flex items-center justify-center gap-2 text-primary-foreground/70 text-sm">
                        <Spinner size="small" className="text-secondary-400 shrink-0" />
                        {t("landing.verifyingCode")}
                      </span> : <button type="button" onClick={handleBackToEmail} className="block w-full focus:outline-hidden text-primary-foreground/50 text-center text-sm hover:text-primary-foreground/70 transition-colors">
                        {t("landing.goBackTryAnother")}
                      </button>}
                  </div>
                </>}
            </motion.div>}

          {/* Email input section */}
          {currentView === "email" && <motion.div key="email-section" variants={stepVariants} initial="initial" animate="animate" exit="exit">
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Input ref={emailInputRef} type="email" placeholder={t("landing.emailPlaceholder")} value={email} onChange={handleEmailChange} onBlur={() => {
                if (email && !validateEmail(email)) {
                  setEmailError(t("landing.emailInvalid"));
                } else {
                  setEmailError(null);
                }
              }} required disabled={isLoading} autoComplete="email" className={cn(ONBOARDING_FORM_INPUT_CLASSNAME, emailError && "border-destructive")} />
                  {emailError && <p className="mt-1 text-destructive text-sm">{emailError}</p>}
                </div>

                <div className="flex justify-end">
                  <Button type="submit" className="rounded-full bg-secondary-400 hover:bg-secondary-500 text-primary-foreground px-6" disabled={isLoading || !isEmailValid} onClick={async () => {
                await trackEventWithDelay("210-uv_clicks_continue_with_email_from_signinup_modal", signupEventProperties());
              }}>
                    {isLoading ? t("landing.sendingCode") : t("landing.continueButton")}
                  </Button>
                </div>
              </form>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }

  // Non-conversational mode (original behavior)
  if (currentView === "otp") {
    return <div className="space-y-6 w-full max-w-sm">
        <div className="space-y-2 text-center">
          <div className="space-y-2">
            <p className="text-primary-foreground">
              {propMode === "sign-in" ? t("landing.signinCode") : t("landing.signupCode")}
              <span className="font-medium">{submittedEmail}</span>
            </p>
            {otpError && !isOtpVerified && <p className="text-destructive text-sm">{otpError}</p>}
          </div>
        </div>

        {isOtpVerified ? <div className="flex min-h-10 flex-col items-center justify-center" role="status" aria-live="polite">
            <span className="inline-flex items-center justify-center gap-2 text-primary-foreground text-sm font-medium">
              <Check className="size-5 shrink-0 text-green-500" aria-hidden="true" />
              {t("landing.codeVerified")}
            </span>
          </div> : <>
            <div className="flex justify-center text-primary-foreground">
              <InputOTP ref={otpInputRef} autoFocus maxLength={6} value={otp} onChange={value_1 => {
            setOtp(value_1);
            // Clear error when user starts typing
            if (otpError && value_1.length > 0) {
              setOtpError(null);
            }
            if (value_1.length === 6) {
              handleOtpSubmit(value_1);
            }
          }} disabled={isLoading} className={otpError ? "border-destructive" : ""}>
                {[...Array(6)].map((__0, i_0) => <InputOTPGroup key={i_0}>
                    <InputOTPSlot className={`rounded-lg border-l ${otpError ? "border-destructive" : ""}`} index={i_0} />
                  </InputOTPGroup>)}
              </InputOTP>
            </div>

            <div className="flex min-h-5 items-center justify-center" role={isLoading ? "status" : undefined} aria-live={isLoading ? "polite" : undefined} aria-busy={isLoading ? true : undefined}>
              {isLoading ? <span className="inline-flex items-center justify-center gap-2 text-primary-foreground/70 text-sm">
                  <Spinner size="small" className="text-secondary-400 shrink-0" />
                  {t("landing.verifyingCode")}
                </span> : <button type="button" onClick={handleBackToEmail} className="focus:outline-hidden w-full text-primary-foreground/50 text-center text-sm hover:text-primary-foreground/70 transition-colors">
                  {t("landing.goBackTryAnother")}
                </button>}
            </div>
          </>}
      </div>;
  }
  return <form onSubmit={handleEmailSubmit} className="space-y-6 w-full max-w-sm">
      <div className="space-y-4">
        <div className="space-y-2">
          <Input ref={emailInputRef} type="email" placeholder={t("landing.emailPlaceholder")} value={email} onChange={handleEmailChange} onBlur={() => {
          if (email && !validateEmail(email)) {
            setEmailError(t("landing.emailInvalid"));
          } else {
            setEmailError(null);
          }
        }} required disabled={isLoading} autoComplete="email" autoFocus className={cn(ONBOARDING_FORM_INPUT_CLASSNAME, emailError && "border-destructive")} />
          {emailError && <p className="mt-1 text-destructive text-sm">{emailError}</p>}
        </div>

        <div className="flex justify-end">
          <Button type="submit" className="rounded-full bg-secondary-400 hover:bg-secondary-500 text-primary-foreground px-6" disabled={isLoading || !isEmailValid} onClick={async () => {
          await trackEventWithDelay("210-uv_clicks_continue_with_email_from_signinup_modal", signupEventProperties());
        }}>
            {isLoading ? t("landing.sendingCode") : t("landing.continueButton")}
          </Button>
        </div>
      </div>
    </form>;
}